import React, { useState, useEffect } from 'react'
import { useImpersonation } from '@/hooks/useImpersonation'
import { useAuth } from '@/hooks/useAuth'
import { AlertTriangle, User, X } from 'lucide-react'

/**
 * Banner component that displays when an admin is impersonating another user
 * Shows at the top of the app with clear indicators and controls to stop impersonation
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonatedEmployeeId, stopImpersonation } = useImpersonation()
  const { user: currentUser } = useAuth()
  const [impersonatedEmployeeName, setImpersonatedEmployeeName] = useState<string | null>(null)

  // Get the impersonated employee's name from the current user's employee data
  useEffect(() => {
    if (isImpersonating && currentUser?.employee) {
      // When impersonating, the employee data in currentUser is actually the impersonated user's data
      const employeeName = currentUser.employee.personalInfo?.name || currentUser.email
      setImpersonatedEmployeeName(employeeName)
    } else {
      setImpersonatedEmployeeName(null)
    }
  }, [isImpersonating, currentUser, impersonatedEmployeeId])

  if (!isImpersonating || !impersonatedEmployeeId || !currentUser) {
    return null
  }

  // Display name with fallback to ID
  const displayName = impersonatedEmployeeName || impersonatedEmployeeId

  return (
    <div className="relative z-50 bg-gradient-to-r from-warning to-orange-500 text-white shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
        {/* Left side - Impersonation indicator */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 animate-pulse" />
            <span className="font-bold text-sm">MODO DE PERSONIFICAÇÃO</span>
          </div>

          <div className="hidden sm:flex items-center gap-2 text-sm">
            <User className="w-4 h-4" />
            <span>
              Admin <strong>{currentUser.email}</strong> personificando funcionário <strong>{displayName}</strong>
            </span>
          </div>
        </div>

        {/* Mobile version */}
        <div className="sm:hidden flex flex-col text-xs">
          <div>Admin: <strong>{currentUser.email.split('@')[0]}</strong></div>
          <div>Funcionário: <strong>{displayName}</strong></div>
        </div>

        {/* Right side - Stop impersonation button */}
        <button
          onClick={stopImpersonation}
          className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-all duration-200 text-sm font-medium"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Parar Personificação</span>
          <span className="sm:hidden">Sair</span>
        </button>
      </div>
    </div>
  )
}

export default ImpersonationBanner
